import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Heading } from '@/components/Elements/Heading';
import { Button } from '@/components/Elements';
import { showNotification } from '@/utils/notificationUtils';
import { ReactComponent as MenuBackIcon } from '@/assets/img/menu-back-icon.svg';
import { ReactComponent as DragHandleIcon } from '@/assets/img/drag-handle.svg';
import AddIcon from '@/assets/img/add-icon-new-v2.svg';
import { ReactComponent as DeleteIcon } from '@/assets/img/delete-icon-v2.svg';
import DownArrow from '@/assets/img/down-arrow.svg';

import './FormFieldDialog.scss';
import { UserContext } from '@/routes';
import { useAuth } from '@/lib/auth';
import { FieldOptions, FormField } from '../types';
import { Dropdown } from 'react-bootstrap';
import DynamicFormField from './DynamicFormField';
import { TypeOf } from 'zod';
import Utils from '@/utils/util';
import { useCreateFormFields } from '../api/getFields';
import { FieldOption } from '@stripe/stripe-js';
import { title } from 'process';
import { useTranslation } from 'react-i18next';

type FormFieldDialogProps = {
  open: boolean;
  onClose: (data?: FormField) => void;
  data?: FormField;
  formFieldCategory: 'pet' | 'owner';
};
const FormFieldDialog = ({ data, open, onClose, formFieldCategory }: FormFieldDialogProps) => {
  const userContext = useContext(UserContext);
  const { user } = useAuth();
  const [formFieldData, setFormFieldData] = useState<FormField>();
  const createFieldMutation = useCreateFormFields();

  const { t, i18n } = useTranslation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (!data) {
      const emptyFormField: FormField = {
        fieldName: '',
        fieldType: '',
        fieldValue: '',
        placeholder: '',
        disabled: true,
        fieldLabel: '',
        fieldDescription: '',
        options: [],
        category: formFieldCategory,
        orderIndex: 0,
      };
      setFormFieldData(emptyFormField);
    } else {
      updateFormData();
    }
  }, []);

  useEffect(() => {
    setFormFieldData({
      fieldName: data?.fieldName ? data?.fieldName : '',
      fieldType: data?.fieldType ? data?.fieldType : '',
      fieldValue: data?.fieldValue,
      placeholder: data?.placeholder,
      disabled: true,
      fieldLabel: data?.fieldLabel ? data?.fieldLabel : '',
      fieldDescription: data?.fieldDescription ? data?.fieldDescription : '',
      options: data?.options,
      category: data?.category ? data?.category : formFieldCategory,
      id: data?.id ? data.id : '',
      orderIndex: 0,
    });
  }, [data]);

  const updateFormData = () => {
    if (data) {
      setFormFieldData(data);
    }
  };
  const handleInputChange = (event: any, key: string) => {
    if (formFieldData) {
      if (key === 'fieldLabel') {
        formFieldData.fieldLabel = event.target.value;
        let preText = 'Select';
        if (formFieldData.fieldType === 'textinput' || formFieldData.fieldType === 'textarea') {
          preText = 'Enter';
        }
        formFieldData.placeholder = preText + ' ' + event.target.value;
      }
      setFormFieldData({ ...formFieldData });
    }
  };
  const selectInputElementTtype = (fieldType: string) => {
    if (formFieldData) {
      formFieldData.fieldType = fieldType;
      if (formFieldData.fieldLabel) {
        let preText = 'Select';
        if (fieldType === 'textinput' || fieldType === 'textarea') {
          preText = 'Enter';
        }
        formFieldData.placeholder = preText + ' ' + formFieldData.fieldLabel;
      }
      setFormFieldData({ ...formFieldData });
    }
  };
  const selectedInputElement =
    formFieldData && Utils.INPUT_ELEMENTS.find((ie) => ie.key === formFieldData.fieldType);
  const onOptionInputChange = (e: any, ffd: FieldOptions, index: number) => {
    if (formFieldData) {
      const fieldOptions = formFieldData?.options;
      if (e.target.id === 'value') {
        ffd.value = e.target.value;
      }
      if (e.target.id === 'label') {
        ffd.label = e.target.value;
        if (formFieldData.fieldType === 'checkbox') {
          ffd.value = 'option' + (index + 1);
        } else {
          ffd.value = e.target.value ? e.target.value.trim() : '';
        }
      }
      fieldOptions?.splice(index, 1, { ...ffd });
      formFieldData.options = fieldOptions;
      setFormFieldData({ ...formFieldData });
    }
  };

  const onAddOptions = () => {
    if (formFieldData) {
      const isOptiosValid = onValidateOptions();
      if (!isOptiosValid) {
        showNotification({
          type: 'error',
          title: t('Please enter all the options value and key'),
        });
        return;
      }

      const fieldOptions = formFieldData?.options ? formFieldData?.options : [];
      fieldOptions?.push({
        label: '',
        value: '',
      });
      formFieldData.options = fieldOptions;
      setFormFieldData({ ...formFieldData });
    }
  };
  const deleteOptions = (index: number) => {
    if (formFieldData) {
      const fieldOptions = formFieldData?.options;
      fieldOptions?.splice(index, 1);
      formFieldData.options = fieldOptions;
      setFormFieldData({ ...formFieldData });
    }
  };

  const validateForm = () => {
    let validationMessage = '';
    if (formFieldData) {
      const {
        fieldLabel,
        fieldName,
        placeholder,
        fieldType,
        options: fieldOptions,
      } = formFieldData;
      const inputElement = Utils.INPUT_ELEMENTS.find((ie) => ie.key === fieldType);
      const optionsRequired = inputElement ? inputElement.options : false;
      if (!fieldLabel || fieldLabel === '') {
        validationMessage = 'Please enter field name';
      } else if (!fieldType || fieldType === '') {
        validationMessage = t('Please select field type');
      } else if (optionsRequired) {
        if (fieldOptions && fieldOptions.length === 0) {
          validationMessage = t('Please add options');
        } else {
          const isOptiosValid = onValidateOptions();
          if (!isOptiosValid) {
            validationMessage = t('Please enter all the options value and key');
          }
        }
      }
    }
    return validationMessage;
  };
  const onValidateOptions = () => {
    const fieldOptions = formFieldData?.options;
    let isValid = true;
    if (fieldOptions?.length) {
      for (let index = 0; index < fieldOptions.length; index++) {
        const fo = fieldOptions[index];
        if (!fo.label || fo.label === '' || !fo.value || fo.value === '') {
          isValid = false;
          break;
        }
      }
    }
    return isValid;
  };
  const addOrEditField = async () => {
    const vm = validateForm();
    if (vm) {
      showNotification({
        type: 'error',
        title: vm,
      });
      return;
    }
    const res = await createFieldMutation.mutateAsync({ data: formFieldData });
    onClose(res);
  };
  const inputElement = Utils.INPUT_ELEMENTS.find((ie) => ie.key === formFieldData?.fieldType);
  const optionsRequired = inputElement ? inputElement.options : false;
  const getContent = () => {
    return (
      <div className="form-field-create-form">
        <div className="petsked-form">
          {formFieldData && formFieldData.fieldType && (
            <div className="preview-section form-field">
              <span className="preview">{t('preview')}</span>
              <div className="container">
                <DynamicFormField data={formFieldData} disableDrag={true} disabled={true} />
              </div>
            </div>
          )}
          {/* <label className="form-label  mt-5">Field Details</label> */}
          <div className="form-field mt-5 mb-2">
            <label className="form-label my-2 mt-2">Field Name</label>
            <input
              id="name"
              className="mb-3"
              type="text"
              placeholder="Enter field Name"
              maxLength={150}
              onChange={(e) => handleInputChange(e, 'fieldLabel')}
              value={formFieldData?.fieldLabel}
            />
            {/* <label className="form-label my-2">Field Key</label>
            <input
              id="name"
              className="mb-3"
              type="text"
              placeholder={t("Enter field Key")}
              maxLength={150}
              disabled={formFieldData?.id ? true : false}
              onChange={(e) => handleInputChange(e, 'fieldName')}
              value={formFieldData?.fieldName}
            />
            <label className="form-label my-2">{t('Field Placeholder')}</label>

            <textarea
              id="description"
              rows={5}
              placeholder={t("Enter field placeholder text..")}
              maxLength={250}
              onChange={(e) => handleInputChange(e, 'placeholder')}
              value={formFieldData?.placeholder}
            /> */}
          </div>
          {optionsRequired && (
            <div className="field-otions">
              <label className="form-label my-2">{t('Add Field Options')}</label>
              <div className="field-option-action">
                {formFieldData?.options?.map((ffd, index) => (
                  <div>
                    <div className="pet-row">
                      {/* <div className="pet-col-6">
                        <input
                          value={ffd.label}
                          id="label"
                          onChange={(e) => onOptionInputChange(e, ffd, index)}
                          placeholder={t("Enter Label")}
                          type="text"
                        />
                      </div> */}
                      <div className="pet-col-12 action">
                        <input
                          value={ffd.label}
                          id="label"
                          onChange={(e) => onOptionInputChange(e, ffd, index)}
                          placeholder="Enter Label"
                          type="text"
                        />
                        <DeleteIcon onClick={() => deleteOptions(index)} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="inverse" startIcon={<img src={AddIcon} />} onClick={onAddOptions}>
                  {t('Add options')}
                </Button>
              </div>
            </div>
          )}
          {!formFieldData?.id && (
            <>
              <Heading
                title={t('Field Type')}
                description={t('Select appropriate input type for this field')}
                size="sm"
              />
              <Dropdown className="new-figma-drop-down drodpdwon-btn-pink mt-3 mb-4">
                <Dropdown.Toggle
                  variant="success"
                  className="drop-down-btn fix-height-54"
                  id="dropdown-basic"
                  disabled={formFieldData?.id ? true : false}
                >
                  {selectedInputElement ? selectedInputElement.label : t('Select Field Type')}
                  <img src={DownArrow} className="down-arrow" />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ width: '100%' }}>
                  <div className="menu-scroller">
                    {Utils.INPUT_ELEMENTS.map((ie, index) => (
                      <Dropdown.Item
                        key={`ie-${index}`}
                        className={''}
                        onClick={() => selectInputElementTtype(ie.key)}
                      >
                        {ie.label}
                      </Dropdown.Item>
                    ))}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      {userContext?.isLandscape ? (
        <Modal
          show={open}
          onHide={() => onClose(undefined)}
          className="petsked-modal-dialog  form-field-dialog"
        >
          <Modal.Header closeButton>
            <Heading
              size="lg"
              title={formFieldData && formFieldData.id ? `Add new field` : `Update Field`}
              description={`Enter the details for the new form field below`}
            />
          </Modal.Header>

          <Modal.Body>{getContent()}</Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              size="lg"
              disabled={createFieldMutation.isLoading}
              onClick={() => {
                addOrEditField();
              }}
            >
              {formFieldData && formFieldData.id
                ? createFieldMutation.isLoading
                  ? 'Updating...'
                  : 'Update Field'
                : createFieldMutation.isLoading
                ? 'Creating...'
                : 'Create Field'}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        <>
          <div className="form-field-create-form-mobile">
            <div
              className="menu-back-nav"
              onClick={() => {
                onClose(undefined);
              }}
            >
              <MenuBackIcon /> <span>{t('Back')}</span>
            </div>
            <Heading
              size="lg"
              title={t(`Add new field`)}
              description={t(`Enter the details for the new form field below`)}
            />
            {getContent()}
            <div className="action">
              <Button variant="primary" size="lg" onClick={addOrEditField}>
                {formFieldData && formFieldData.id
                  ? createFieldMutation.isLoading
                    ? 'Updating...'
                    : 'Update Field'
                  : createFieldMutation.isLoading
                  ? 'Creating...'
                  : 'Create Field'}{' '}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default FormFieldDialog;
